<template>
    <Modal class="space-y-4" @close="closeGridSetup">
        <div>
            <h2 class="text-lg font-semibold">
                {{ lang('general.datagrid.setup') }}
            </h2>
            <p class="text-sm">
                {{ lang('general.datagrid.setup_description') }}
            </p>
            <ul class="flex pt-1 space-x-2 text-xs">
                <li>
                    <a href="#" class="underline hover:no-underline" @click="setupReset">
                        {{ lang('general.datagrid.setup_reset') }}
                    </a>
                </li>
                <li>
                    <a href="#" class="underline hover:no-underline" @click="setupResetAll">
                        {{ lang('general.datagrid.setup_reset_all') }}
                    </a>
                </li>
            </ul>
        </div>
        <div>
            <table class="w-full border dark:border-neutral-800">
                <Sortable :list="columns" item-key="slug" tag="tbody" @end="sorted">
                    <template #item="{ element: column }">
                        <tr>
                            <td
                                class="w-3 text-center bg-gray-100 border dark:border-neutral-800 border-neutral-200 dark:bg-neutral-900 cursor-grab"
                            >
                                <icon
                                    icon="hero/outline/dots-vertical"
                                    class="w-4 h-4 mx-auto pointer-events-none"
                                ></icon>
                            </td>
                            <th
                                class="px-2 py-1 text-xs text-left border pointer-events-none dark:border-neutral-800 border-neutral-200 bg-gray-50 dark:bg-neutral-950"
                            >
                                <slot :name="`heading-${column.slug}`">
                                    {{ column.name }}
                                </slot>
                            </th>
                            <td class="border dark:border-neutral-800 border-neutral-200">
                                <label class="block px-2 py-1 cursor-pointer hover:bg-gray-50">
                                    <AdminCheckbox
                                        v-model="column.display"
                                        :value="true"
                                        :checked="column.display === true"
                                    />
                                </label>
                            </td>
                            <td class="px-2 py-1 border dark:border-neutral-800 border-neutral-200">
                                <template v-if="column.slug">
                                    {{ column.slug }}
                                </template>
                            </td>
                        </tr>
                    </template>
                </Sortable>
            </table>
        </div>
        <div class="flex justify-between">
            <Button color="default" @click="closeGridSetup">
                {{ lang('general.close') }}
            </Button>
            <Button color="primary" @click="storeGridSetup">
                {{ lang('general.save') }}
            </Button>
        </div>
    </Modal>
</template>

<script setup lang="ts">
import { toRefs } from 'vue';
import { Column } from './../types';
import { useCookies } from '@vueuse/integrations/useCookies';
import { arrayMove } from '@src/Common/Helpers/array';
// import { usePage } from '@inertiajs/vue3';

interface Props {
    columns?: Column[] | null;
    dataGridId?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
    columns: null,
    dataGridId: null,
});

const emit = defineEmits<{
    (event: 'close', { columns }): void;
    (event: 'sorted', { columns }): void;
    (event: 'reset'): void;
}>();

const { columns, dataGridId } = toRefs(props);

const cookies = useCookies(['locale', 'datagrid.params.take']);

const closeGridSetup = () => {
    emit('close', { columns: columns.value });
};

const storeGridSetup = () => {
    const columnSetup = columns.value.map((column) => ({
        slug: column.slug,
        display: column.display,
    }));

    let columnsCookie = cookies.get('datagrid.columns');

    if (typeof columnsCookie === 'undefined') {
        columnsCookie = {};
    }

    columnsCookie[dataGridId.value] = columnSetup;

    cookies.set('datagrid.columns', columnsCookie);

    closeGridSetup();

    // Save to database
    axios
        .post(route('users/preferences.save'), {
            key: `datagrid.columns.${dataGridId.value}`,
            value: columnSetup,
        })
        .then(({ data }) => {
            console.log(data);
        });
};

/**
 * Reset
 */
const setupReset = () => {
    let columnsCookie = cookies.get('datagrid.columns');

    if (typeof columnsCookie === 'undefined') {
        columnsCookie = {};
    }

    if (typeof columnsCookie[dataGridId.value] !== 'undefined') {
        delete columnsCookie[dataGridId.value];
    }

    cookies.set('datagrid.columns', columnsCookie);

    emit('reset');
};

const setupResetAll = () => {
    cookies.remove('datagrid.columns');

    emit('reset');
};

const sorted = (event) => {
    let slugs = columns.value.map((item) => item.slug);
    slugs = arrayMove(slugs, event.oldIndex, event.newIndex);

    // Order columns by slug order
    columns.value.sort((a, b) => {
        return slugs.indexOf(a.slug) - slugs.indexOf(b.slug);
    });

    emit('sorted', { columns: columns.value });
};
</script>
